// Layout for project settings with sidebar navigation.

import { Outlet, Link, useParams, useLocation } from 'react-router-dom';
import { observer } from '@react';
import { useProject, useI18n } from '@react/hooks';
import { Badge } from '@react/components';
import { getProjectName } from '@/features/project/logic';
import { ProjectIcon } from '@/components/IconPicker';
import { cn } from '@core/ui';

// --- Icons ---

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  );
}

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  );
}

function BoardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
      <line x1="9" y1="3" x2="9" y2="21" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </svg>
  );
}

function ComponentIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.5 8.5L9 12l-3.5 3.5L2 12zM12 2l3.5 3.5L12 9 8.5 5.5zM18.5 8.5L22 12l-3.5 3.5L15 12zM12 15l3.5 3.5L12 22l-3.5-3.5z" />
    </svg>
  );
}

function LabelIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  );
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

interface NavItem {
  id: string;
  labelKey: string;
  href: string;
  icon: React.FC<{ className?: string }>;
  danger?: boolean;
}

export const ProjectSettingsLayout = observer(function ProjectSettingsLayout() {
  const { id } = useParams<{ id: string }>();
  const { t } = useI18n();
  const location = useLocation();
  const store = useProject();

  const projectName = getProjectName(store.currentItem) || t('project.title');
  const item = store.currentItem as any;

  const navItems: NavItem[] = [
    { id: 'details', labelKey: 'project.details', href: `/projects/${id}/settings`, icon: InfoIcon },
    { id: 'members', labelKey: 'project.section.members', href: `/projects/${id}/settings/members`, icon: UsersIcon },
    { id: 'boards', labelKey: 'project.section.boards', href: `/projects/${id}/settings/boards`, icon: BoardIcon },
    { id: 'components', labelKey: 'project.section.components', href: `/projects/${id}/settings/components`, icon: ComponentIcon },
    { id: 'labels', labelKey: 'project.section.labels', href: `/projects/${id}/settings/labels`, icon: LabelIcon },
    { id: 'danger', labelKey: 'project.dangerZone.title', href: `/projects/${id}/settings/danger`, icon: AlertTriangleIcon, danger: true },
  ];

  const isActive = (href: string) => {
    if (href === `/projects/${id}/settings`) {
      return location.pathname === href || location.pathname === href + '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="flex flex-col md:flex-row gap-0 md:gap-6 min-h-full">
      {/* Sidebar */}
      <aside className="w-full md:w-60 flex-shrink-0">
        {/* Back link */}
        <Link
          to={`/projects/${id}`}
          className="flex items-center gap-2 px-3 py-2 mb-3 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeftIcon className="h-4 w-4" />
          {projectName}
        </Link>

        {/* Project identity mini-card */}
        {item && (
          <div className="px-3 mb-5 flex items-center gap-3">
            <div
              className="flex items-center justify-center h-10 w-10 rounded-lg shrink-0 border border-border"
              style={{ backgroundColor: (item.color || '#6366f1') + '20' }}
            >
              <ProjectIcon iconId={item.icon} className="h-5 w-5" style={{ color: item.color || '#6366f1' }} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{projectName}</p>
              <Badge variant="secondary" size="sm">{item.key || '—'}</Badge>
            </div>
          </div>
        )}

        <p className="px-3 mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          {t('project.settings')}
        </p>

        <nav>
          <ul className="space-y-0.5">
            {navItems.map((nav) => {
              const active = isActive(nav.href);
              const Icon = nav.icon;
              return (
                <li key={nav.id}>
                  <Link
                    to={nav.href}
                    className={cn(
                      'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                      active
                        ? nav.danger
                          ? 'bg-destructive/10 text-destructive font-medium'
                          : 'bg-accent text-accent-foreground font-medium'
                        : nav.danger
                          ? 'text-muted-foreground hover:bg-destructive/5 hover:text-destructive'
                          : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground',
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <span>{t(nav.labelKey)}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>

      {/* Divider */}
      <div className="hidden md:block w-px bg-border flex-shrink-0" />

      {/* Content */}
      <main className="flex-1 min-w-0 py-2 md:py-0">
        <Outlet />
      </main>
    </div>
  );
});
